/**
 * Controle do tapume de "Em manutenção".
 *
 * O tapume é ativado por variável de ambiente (`REACT_APP_MAINTENANCE_MODE`).
 * Para conseguir navegar normalmente mesmo com o tapume ativo (ex.: time interno
 * validando a aplicação), basta acessar a aplicação com `?debug=1` na URL — o
 * bypass fica salvo no localStorage e passa a valer para os próximos acessos.
 * Use `?debug=0` para remover o bypass.
 */
import { env } from '../config/env';
import { ApiLocalStorage } from './ApiLocalStorage';

/** Chave do localStorage que guarda o bypass do tapume de manutenção. */
export const MAINTENANCE_BYPASS_KEY = '@pwa:maintenance-bypass';

/** Parâmetro de URL que ativa/desativa o bypass do tapume. */
const DEBUG_PARAM = 'debug';

/**
 * Lê o parâmetro `?debug` da URL atual e persiste o bypass do tapume:
 * - `?debug=1` salva o bypass no localStorage
 * - `?debug=0` remove o bypass
 *
 * Deve ser chamada uma única vez, antes da renderização da aplicação.
 */
export const consumeMaintenanceDebugParam = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);

    if (!params.has(DEBUG_PARAM)) return;

    if (params.get(DEBUG_PARAM) === '1') {
      ApiLocalStorage.set(MAINTENANCE_BYPASS_KEY, true);
    } else {
      ApiLocalStorage.remove(MAINTENANCE_BYPASS_KEY);
    }
  } catch {
    // Ambiente sem window/localStorage — ignora silenciosamente.
  }
};

/** Indica se o bypass do tapume está salvo no localStorage. */
export const hasMaintenanceBypass = (): boolean =>
  ApiLocalStorage.get(MAINTENANCE_BYPASS_KEY) === true;

/** Indica se o modo de manutenção está habilitado via variável de ambiente. */
export const isMaintenanceEnabledByEnv = (): boolean =>
  String(env.MAINTENANCE_MODE).toLowerCase() === 'true';

/**
 * Indica se o tapume de manutenção deve ser exibido:
 * habilitado por env **e** sem bypass salvo no localStorage.
 */
export const isMaintenanceMode = (): boolean =>
  isMaintenanceEnabledByEnv() && !hasMaintenanceBypass();
